import { cn } from '@/lib/cn';
import { Card } from '@heidi/design-system/card';
import { forwardRef, memo, type ReactNode } from 'react';

export const HASH_HIGHLIGHT_CLASSES = 'ring-2 ring-inset ring-border-focus';

interface SettingsSectionCardProps {
  children: ReactNode;
  className?: string;
  id?: string;
  highlighted?: boolean;
}

/**
 * Card shell for a settings section, with the hash-deep-link highlight ring.
 *
 * Uses `forwardRef` rather than declaring `ref` as a prop: on React 18 `ref` is
 * reserved and never reaches a function component's props, so a caller that
 * passed one got a permanently `null` ref — and `useHashBasedScrolling` then had
 * nothing to measure. Most pages hang the scroll ref on a wrapper `<div>`, but
 * Heidi Labs puts it straight on the card, so the ref has to land on the `Card`.
 */
export const SettingsSectionCard = memo(
  forwardRef<HTMLDivElement, SettingsSectionCardProps>(
    ({ children, className, id, highlighted }, ref) => {
      return (
        <Card
          className={cn(
            'p-5 shadow-none hover:shadow-none @md:p-8',
            highlighted && HASH_HIGHLIGHT_CLASSES,
            className
          )}
          id={id}
          ref={ref}
        >
          {children}
        </Card>
      );
    }
  )
);

SettingsSectionCard.displayName = 'SettingsSectionCard';
